import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  FlexContainer,
  Grid
} from '../../styles/StyledComponents';
import { 
  FaArrowLeft, 
  FaEdit, 
  FaDownload, 
  FaPrint, 
  FaEye, 
  FaFileAlt,
  FaCalendarAlt,
  FaUser,
  FaBuilding,
  FaTools,
  FaTruck,
  FaHandshake,
  FaCog,
  FaMoneyBillWave,
  FaReceipt,
  FaMapMarkerAlt
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';

const DetailContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #141340;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  .title-icon {
    background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    box-shadow: 0 8px 24px rgba(246, 139, 33, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(246, 139, 33, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(246, 139, 33, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ProjectInfoCard = styled(Card)`
  margin-bottom: 2rem;
  border: 2px solid #F68B21;
  background: linear-gradient(135deg, #fff9f0 0%, #ffffff 100%);
  
  .project-header {
    background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
    color: white;
    padding: 1rem;
    border-radius: 8px 8px 0 0;
    
    h3 {
      margin: 0;
      font-size: 1.2rem;
      font-weight: 600;
    }
    
    .project-meta {
      margin-top: 0.5rem;
      font-size: 0.85rem;
      opacity: 0.9;
    }
  }
`;

const StatusBadge = styled(Badge)`
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
  
  h3 {
    color: #141340;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const DetailItem = styled.div`
  .label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #6c757d;
    margin-bottom: 0.25rem;
  }
  
  .value {
    font-size: 1rem;
    color: #141340;
    font-weight: 500;
  }
  
  .amount {
    font-size: 1.5rem;
    font-weight: 700;
    color: #F68B21;
  }
`;

const AttachmentSection = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  
  .attachment-title {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
  }
  
  .attachment-info {
    font-size: 0.9rem;
    color: #6c757d;
  }
`;

const CashierExpenseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [expense, setExpense] = useState<any>(null);
  const [assignedProject, setAssignedProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadExpenseData = async () => {
      try {
        setLoading(true);
        
        // Get projects assigned to the current cashier
        const assignedProjectsResponse = await apiService.getAssignedProjects();
        const assignedProjects = assignedProjectsResponse.data || [];
        
        if (assignedProjects.length > 0) {
          // For now, use the first assigned project
          setAssignedProject(assignedProjects[0]);
          
          // Load the specific expense
          if (id) {
            try {
              const expenseResponse = await apiService.getExpense(id);
              setExpense(expenseResponse.data);
            } catch (expenseError) {
              console.error('Error loading expense:', expenseError);
              setExpense(null);
            }
          }
        } else {
          setAssignedProject(null);
        }
      } catch (error) {
        console.error('Error loading expense data:', error);
        setAssignedProject(null);
        setExpense(null);
      } finally {
        setLoading(false);
      }
    };
    
    if (user && id) {
      loadExpenseData();
    }
  }, [id, user]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!expense) {
    return (
      <DetailContainer>
        <Card>
          <CardBody style={{ textAlign: 'center', padding: '3rem' }}>
            <p>Expense not found.</p>
          </CardBody>
        </Card>
      </DetailContainer>
    );
  }

  if (!assignedProject) {
    return (
      <DetailContainer>
        <Card>
          <CardBody style={{ textAlign: 'center', padding: '3rem' }}>
            <p>No project assigned. Please contact your supervisor to get assigned to a project.</p>
          </CardBody>
        </Card>
      </DetailContainer>
    );
  }

  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Pending': return 'warning';
      case 'Rejected': return 'danger';
      default: return 'info';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Materials': return FaBuilding;
      case 'Labor': return FaTools;
      case 'Equipment': return FaTruck;
      case 'Sub-Contracts': return FaHandshake;
      case 'Others': return FaCog;
      default: return FaMoneyBillWave;
    }
  };

  return (
    <DetailContainer>
      <PageHeader>
        <PageTitle>
          <div className="title-icon">
            <IconWrapper icon={FaReceipt} />
          </div>
          Expense Details
        </PageTitle>
        <FlexContainer gap="1rem">
          <OutlineButton as={Link} to="/cashier/expenses">
            <IconWrapper icon={FaArrowLeft} />
            Back to Expenses
          </OutlineButton>
                      <ActionButton as={Link} to={`/cashier/expense/edit/${expense._id || expense.id}`}>
            <IconWrapper icon={FaEdit} />
            Edit Expense
          </ActionButton>
        </FlexContainer>
      </PageHeader>

      {assignedProject ? (
        <ProjectInfoCard>
          <div className="project-header">
            <h3>{assignedProject.name}</h3>
            <div className="project-meta">
              Project ID: {assignedProject._id || assignedProject.id} • Location: {assignedProject.location}
            </div>
          </div>
          <CardBody>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>Project Manager:</strong> {
                  assignedProject.assignedStaff?.find((staff: any) => staff.role === 'Project Manager')?.name || 'Not assigned'
                }
              </div>
              <div>
                <strong>Budget:</strong> {formatCurrency(assignedProject.budget.total)}
              </div>
            </div>
          </CardBody>
        </ProjectInfoCard>
      ) : (
        <Card>
          <CardBody style={{ textAlign: 'center', padding: '2rem' }}>
            <p>No project assigned. Please contact your supervisor to get assigned to a project.</p>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Expense Information</h3>
            <StatusBadge variant={getStatusVariant(expense.status)}>
              {expense.status}
            </StatusBadge>
          </div>
        </CardHeader>
        <CardBody>
          <DetailSection>
            <h3>
              <IconWrapper icon={FaMoneyBillWave} />
              Basic Information
            </h3>
            <DetailGrid>
              <DetailItem>
                <div className="label">Description</div>
                <div className="value">{expense.description}</div>
              </DetailItem>
              <DetailItem>
                <div className="label">Amount</div>
                <div className="amount">{formatCurrency(expense.amount)}</div>
              </DetailItem>
              <DetailItem>
                <div className="label">Category</div>
                <div className="value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <IconWrapper icon={getCategoryIcon(expense.category)} />
                  {expense.category}
                </div>
              </DetailItem>
              <DetailItem>
                <div className="label">Receipt Number</div>
                <div className="value">{expense.receiptNumber}</div>
              </DetailItem>
            </DetailGrid>
          </DetailSection>

          <DetailSection>
            <h3>
              <IconWrapper icon={FaCalendarAlt} />
              Dates & Timeline
            </h3>
            <DetailGrid>
              <DetailItem>
                <div className="label">Expense Date</div>
                <div className="value">{new Date(expense.date).toLocaleDateString()}</div>
              </DetailItem>
              <DetailItem>
                <div className="label">Recorded On</div>
                <div className="value">{new Date(expense.recordedAt).toLocaleString()}</div>
              </DetailItem>
              {expense.approvedAt && (
                <DetailItem>
                  <div className="label">Approved On</div>
                  <div className="value">{new Date(expense.approvedAt).toLocaleString()}</div>
                </DetailItem>
              )}
            </DetailGrid>
          </DetailSection>

          <DetailSection>
            <h3>
              <IconWrapper icon={FaUser} />
              People & Approval
            </h3>
            <DetailGrid>
              <DetailItem>
                <div className="label">Recorded By</div>
                <div className="value">{expense.recordedBy && typeof expense.recordedBy === 'object' ? expense.recordedBy.name : expense.recordedBy || 'Unknown'}</div>
              </DetailItem>
              {expense.approvedBy && (
                <DetailItem>
                  <div className="label">Approved By</div>
                  <div className="value">{expense.approvedBy && typeof expense.approvedBy === 'object' ? expense.approvedBy.name : expense.approvedBy || 'Unknown'}</div>
                </DetailItem>
              )}
              <DetailItem>
                <div className="label">Supplier/Service Provider</div>
                <div className="value">{expense.supplier}</div>
              </DetailItem>
            </DetailGrid>
          </DetailSection>

          {expense.notes && (
            <DetailSection>
              <h3>
                <IconWrapper icon={FaFileAlt} />
                Notes
              </h3>
              <div style={{ 
                padding: '1rem', 
                background: '#f8f9fa', 
                borderRadius: '8px', 
                border: '1px solid #e9ecef',
                lineHeight: '1.6'
              }}>
                {expense.notes}
              </div>
            </DetailSection>
          )}

          {expense.attachment && (
            <DetailSection>
              <h3>
                <IconWrapper icon={FaDownload} />
                Attachments
              </h3>
              <AttachmentSection>
                <div className="attachment-title">
                  <IconWrapper icon={FaFileAlt} />
                  {expense.attachment}
                </div>
                <div className="attachment-info">
                  Size: {expense.attachmentSize} • Uploaded on {new Date(expense.recordedAt).toLocaleDateString()}
                </div>
                <FlexContainer gap="1rem" style={{ marginTop: '1rem' }}>
                  <OutlineButton>
                    <IconWrapper icon={FaEye} />
                    View
                  </OutlineButton>
                  <OutlineButton>
                    <IconWrapper icon={FaDownload} />
                    Download
                  </OutlineButton>
                  <OutlineButton>
                    <IconWrapper icon={FaPrint} />
                    Print
                  </OutlineButton>
                </FlexContainer>
              </AttachmentSection>
            </DetailSection>
          )}
        </CardBody>
      </Card>
      
      <div style={{ 
        textAlign: 'right', 
        padding: '2rem 1rem 1rem', 
        color: '#6c757d', 
        fontSize: '0.8rem' 
      }}>
        Developed by Sandfish
      </div>
    </DetailContainer>
  );
};

export default CashierExpenseDetail; 